/**
 * Компонент Dashboards — рефакторенная версия.
 *
 * Изменения:
 * - Подключены хуки useDashboardLayout, useDashboardBlocks, useDashboardFullscreen, useDashboardZoom
 * - Убрано дублирование логики layout, блоков, resize
 * - attrItems вынесены в constants/attribution.tsx
 * - window.location заменён на useLocation (react-router)
 * - setTimeout заменён на safeSetTimeout
 * - Извлечён общий рендер-блок (FullReportModal)
 * - Добавлен abort controller для загрузки дашборда
 * - Cleanup overflow при размонтировании — в useDashboardLayout
 * - DashboardBlockProvider интегрирован в DashboardBlock
 */
import { FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { throttle } from 'lodash';
import { useSearchParams } from 'react-router-dom';

import {
  defaultGroupings,
  defaultMetrics,
} from '../../app/providers/redux/slices/reports/table/tableSlice';
import { safeLocalStorage } from '../../helpers/utils/safeStorage';
import { showErrorMessage } from '../../helpers/utils/ui';
import { unApi } from '../../shared/common/api/endpoints/reports/unApi';
import MainLayout from '../../shared/common/components/layouts/MainLayout';
import Loader from '../../shared/common/components/loader/Loader';
import { normalizeMetricRefs } from '../../shared/common/components/reports/metrics/utils';
import DashboardParams from '../../shared/common/components/reports/params/DashboardParams';
import { FullReportModal } from '../../shared/common/components/reports/FullReportModal';
import { dashboardChartDetails } from '../../shared/common/constants/details';
import {
  QUERY_FROM_MENU,
  QUERY_NAME_DASHBOARD,
  QUERY_NAME_HOME,
} from '../../shared/common/constants/query-params';
import { STATIC_LINK_DRAWER } from '../../shared/common/constants/staticLinkConstants';
import { useActions } from '../../shared/common/hooks/useActions';
import { useTypedSelector } from '../../shared/common/hooks/useTypedSelector';
import { useTimerCleanup } from '../../shared/common/hooks/useTimerCleanup';
import { useSaveDashboardShortcut } from '../../shared/common/hooks/useSaveDashboardShortcut';

import { attrItems } from './constants/attribution';
import CreateMetricsModal from '../../shared/common/components/reports/CreateMetricsModal';
import DashboardGrid from './components/DashboardGrid';
import DashboardToolbar from './components/DashboardToolbar';
import AddBlockModal from './components/modal/AddBlockModal';
import EditBlockModal from './components/modal/EditBlockModal';
import {
  useDashboardZoom,
  useDashboardFullscreen,
  useDashboardFilters,
  useDashboardLayout,
  useDashboardBlocks,
  useDashboardHistory,
} from './hooks';
import {
  DashboardComponent,
  GRID_CONSTANTS,
} from './types';
import { getReportTypeFromDate } from './utils/layoutMigration';

import styles from './Dashboards.module.scss';

// Re-export для обратной совместимости
export type { DashboardComponent, DashboardBlockData, GridItemLayout } from './types';

// ==================== Компонент ====================

const Dashboards: FC<{ reportId?: string }> = ({ reportId }) => {
  // ==================== Refs ====================
  const containerRef = useRef<HTMLDivElement | null>(null);
  const paramsRef = useRef(null);
  const initialLoadDoneRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // ==================== Router ====================
  const [searchParams, setSearchParams] = useSearchParams();

  // ==================== Timer cleanup ====================
  const { safeSetTimeout } = useTimerCleanup();

  // ==================== Кастомные хуки ====================
  const { scale, zoomIn, zoomOut, resetZoom, setScale } = useDashboardZoom(containerRef);
  const { isFullscreen, toggleFullscreen } = useDashboardFullscreen();

  // Layout хук — управляет grid, drag, resize
  const {
    dashboardComponents,
    setDashboardComponents,
    layout,
    gridCols,
    gridWidth,
    calculatedHeight,
    onLayoutChange,
    isResizing,
    isDragging,
    allowResize,
    onDragStart,
    onDragStop,
    skipNextLayoutChange,
    onResizeStart,
    onResize,
    onResizeStop,
  } = useDashboardLayout();

  // ==================== Redux State ====================
  const { isFixedHeader } = useTypedSelector((state) => state.dashboard);
  const mainDashboardHash = useTypedSelector((state) => state.reports.mainDashboardHash);
  const currentDashboard = useTypedSelector((state) => state.reports.currentDashboard);
  const savedDashboards = useTypedSelector((state) => state.reports.savedDashboards);
  const isLoading = useTypedSelector((state) => state.reports.isLoading);
  const isCreateMetricsVisible = useTypedSelector((state) => state.reports.isCreateMetricsVisible);
  const { isGlobalLoading } = useTypedSelector((state) => state.app);

  // Обогащаем currentDashboard данными из savedDashboards (is_favorite, access_id, user_id и т.д.)
  const savedDashboardData = savedDashboards.find(
    (d: any) => d.id === currentDashboard.id || d.hash === currentDashboard.hash
  );
  const dashboardMenuItem = currentDashboard.id && savedDashboardData
    ? { ...savedDashboardData, ...currentDashboard }
    : currentDashboard;

  // ==================== Local State ====================
  const [isSettingsCollapsed, setIsSettingsCollapsed] = useState(false);
  const [saveAsModalVisible, setSaveAsModalVisible] = useState(false);
  const [selectedBlockIds, setSelectedBlockIds] = useState<Set<string>>(new Set());

  /** Ref для группового перемещения — хранит DOM-элементы и коэффициенты конвертации grid→px */
  const groupDragRef = useRef<{
    elements: { el: HTMLElement; origTransform: string; origTransition: string }[];
    startX: number;
    startY: number;
    unitToPxX: number;
    unitToPxY: number;
  } | null>(null);

  // Автоматически сворачиваем настройки при входе в полноэкранный режим
  useEffect(() => {
    if (isFullscreen) {
      setIsSettingsCollapsed(true);
    }
  }, [isFullscreen]);

  // ==================== Actions ====================
  const {
    setPeriod,
    setFilters,
    setMetrics,
    setGroupings,
    setFiltersTable,
    setCurrentReport,
    setCurrentSegment,
    setSortTable,
    setMetaCompare,
    setSeries,
    setMetricsChecked,
    setMetaChartFetching,
    setMetaTableFetching,
    setAttributesDashboardBlocks,
    setDashboardBlockDetails,
    setDashboardsBlocksInitialState,
    setDashboardsBlocksTableInitialState,
    setDashboardsBlocksChartState,
    setDashboardDetails,
    setCurrentFunnel,
    setIsFixedHeader,
    setDrawerLink,
    setAppReportOpen,
    setCurrentDashboard,
    loadBlockTableData,
  } = useActions();

  // ==================== History хук — undo/redo по Ctrl+Z / Ctrl+Shift+Z ====================
  const handleRestore = useCallback((restoredBlockIds: string[]) => {
    // Переинициализируем Redux-state для восстановленных блоков (после undo delete)
    if (restoredBlockIds.length > 0) {
      setDashboardsBlocksInitialState(restoredBlockIds);
      setDashboardsBlocksTableInitialState({ ids: restoredBlockIds });
      restoredBlockIds.forEach((blockId) => {
        setDashboardsBlocksChartState({ ids: [blockId] });
        setMetaTableFetching({ value: true, blockId });
        setMetaChartFetching({ value: true, blockId });
      });
    }
  }, []);

  const { pushSnapshot } = useDashboardHistory(
    dashboardComponents,
    setDashboardComponents,
    { enabled: true, onRestore: handleRestore }
  );

  // Обёртки drag/resize для снятия снимков перед изменением layout
  const onDragStartWithHistory = useCallback(
    (layout: any[], oldItem: any, newItem: any) => {
      pushSnapshot();
      onDragStart(layout, oldItem, newItem);

      // Подготавливаем DOM для группового перемещения
      if (selectedBlockIds.size > 1 && selectedBlockIds.has(oldItem.i)) {
        const colWidth = (gridWidth - GRID_CONSTANTS.MARGIN * (gridCols - 1)) / gridCols;
        const unitToPxX = colWidth + GRID_CONSTANTS.MARGIN;
        const unitToPxY = GRID_CONSTANTS.CELL_HEIGHT + GRID_CONSTANTS.MARGIN;

        const elements: { el: HTMLElement; origTransform: string; origTransition: string }[] = [];

        selectedBlockIds.forEach((blockId) => {
          if (blockId === oldItem.i) return; // перетаскиваемый блок двигается сам
          const wrapper = document.querySelector(`[data-block-id="${blockId}"]`);
          if (!wrapper) return;
          // react-grid-layout оборачивает контент в .react-grid-item
          const gridItem = wrapper.closest('.react-grid-item') as HTMLElement;
          if (!gridItem) return;

          elements.push({
            el: gridItem,
            origTransform: gridItem.style.transform,
            origTransition: gridItem.style.transition,
          });
          // Убираем transition чтобы перемещение было моментальным
          gridItem.style.transition = 'none';
        });

        groupDragRef.current = {
          elements,
          startX: oldItem.x,
          startY: oldItem.y,
          unitToPxX,
          unitToPxY,
        };
      } else {
        groupDragRef.current = null;
      }
    },
    [pushSnapshot, onDragStart, selectedBlockIds, gridWidth, gridCols]
  );

  // Real-time визуальное перемещение выделенных блоков при drag
  const onDragHandler = useCallback(
    (_layout: any[], _oldItem: any, newItem: any) => {
      const group = groupDragRef.current;
      if (!group || group.elements.length === 0) return;

      const dx = newItem.x - group.startX;
      const dy = newItem.y - group.startY;

      const pxDx = dx * group.unitToPxX;
      const pxDy = dy * group.unitToPxY;

      group.elements.forEach(({ el, origTransform }) => {
        // origTransform имеет формат "translate(Xpx, Ypx)"
        const match = origTransform.match(/translate\(([^,]+),\s*([^)]+)\)/);
        if (match) {
          const origX = parseFloat(match[1]);
          const origY = parseFloat(match[2]);
          el.style.transform = `translate(${origX + pxDx}px, ${origY + pxDy}px)`;
        }
      });
    },
    []
  );

  // Групповое перемещение выделенных блоков + восстановление DOM
  const onDragStopWithSelection = useCallback(
    (_layout: any[], oldItem: any, newItem: any) => {
      // Восстанавливаем DOM-элементы (transition + transform)
      const group = groupDragRef.current;
      if (group) {
        group.elements.forEach(({ el, origTransform, origTransition }) => {
          el.style.transform = origTransform;
          el.style.transition = origTransition;
        });
        groupDragRef.current = null;
      }

      onDragStop(_layout, oldItem, newItem);

      if (selectedBlockIds.size > 1 && selectedBlockIds.has(newItem.i)) {
        const dx = newItem.x - oldItem.x;
        const dy = newItem.y - oldItem.y;

        if (dx !== 0 || dy !== 0) {
          skipNextLayoutChange();
          setDashboardComponents((prev: any[]) =>
            prev.map((comp: any) => {
              if (comp.layoutData.i === newItem.i) {
                return {
                  ...comp,
                  layoutData: { ...comp.layoutData, x: newItem.x, y: newItem.y, scaled: true },
                };
              }
              if (!selectedBlockIds.has(comp.layoutData.i)) return comp;
              return {
                ...comp,
                layoutData: {
                  ...comp.layoutData,
                  x: Math.max(0, comp.layoutData.x + dx),
                  y: Math.max(0, comp.layoutData.y + dy),
                  scaled: true,
                },
              };
            })
          );
        }
      }
    },
    [selectedBlockIds, onDragStop, skipNextLayoutChange, setDashboardComponents]
  );

  const onResizeStartWithHistory = useCallback(
    (currentLayout: any[], oldItem: any) => {
      pushSnapshot();
      onResizeStart(currentLayout, oldItem);
    },
    [pushSnapshot, onResizeStart]
  );

  // Blocks хук — управляет CRUD блоков, модальными окнами
  const {
    addBlockVisible,
    setAddBlockVisible,
    editBlockState,
    setEditBlockState,
    fullReportModalState,
    handleAddBlock,
    handleEditBlock,
    handleDelete,
    handleEdit,
    handleClone,
    handleOpenFullReport,
    handleCloseFullReport,
    forceUpdate,
  } = useDashboardBlocks(dashboardComponents, setDashboardComponents, pushSnapshot);

  // Filters хук — управляет фильтрами, кнопкой «Показать», обновлением данных
  const {
    isMountRef,
    fetchDashboardDataButtonVisible,
    setFetchDashboardDataButtonVisible,
    handleUpdateDashboardData: baseHandleUpdateDashboardData,
  } = useDashboardFilters(dashboardComponents, {
    editBlockVisible: editBlockState.visible,
    addBlockVisible: addBlockVisible.visible,
  });

  // ==================== Хук сохранения по Ctrl+S ====================
  const { save: saveDashboard } = useSaveDashboardShortcut({
    enabled: !addBlockVisible.visible && !editBlockState.visible,
    scale,
    onSaveAs: () => setSaveAsModalVisible(true),
  });

  // ==================== Выделение блоков (Cmd+A) ====================

  // Сбросить выделение при открытии модалок
  useEffect(() => {
    if (editBlockState.visible || addBlockVisible.visible) {
      setSelectedBlockIds(new Set());
    }
  }, [editBlockState.visible, addBlockVisible.visible]);

  // Cmd+A — выделить все блоки, Escape — снять выделение
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (editBlockState.visible || addBlockVisible.visible) return;
      const activeEl = document.activeElement;
      if (activeEl && ['INPUT', 'TEXTAREA', 'SELECT'].includes(activeEl.tagName)) return;

      if ((e.metaKey || e.ctrlKey) && e.key === 'a') {
        e.preventDefault();
        setSelectedBlockIds(new Set(dashboardComponents.map((c) => c.layoutData.i)));
      }

      if (e.key === 'Escape' && selectedBlockIds.size > 0) {
        setSelectedBlockIds(new Set());
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [dashboardComponents, editBlockState.visible, addBlockVisible.visible, selectedBlockIds.size]);

  const handleDeselectAll = useCallback(() => {
    setSelectedBlockIds(new Set());
  }, []);

  // ==================== Инициализация ====================
  useLayoutEffect(() => {
    setMetaCompare({ value: false });
    setMetrics({ data: defaultMetrics });
    setGroupings({ data: defaultGroupings });
    setFilters([]);
    setFiltersTable([]);
    setCurrentReport({});
    setSortTable({});
    setCurrentSegment(null);
    setSeries([]);
    safeSetTimeout(() => {
      setMetaChartFetching({ value: true });
    }, 700);
    setMetricsChecked({
      data: [
        {
          dataKey: defaultMetrics[0].dataKey,
          name: defaultMetrics[0].dataKey,
          label: defaultMetrics[0].label,
        },
      ],
    });

    return () => {
      setMetaCompare({ value: false });
      setMetrics({ data: defaultMetrics });
      setGroupings({ data: defaultGroupings });
      setFilters([]);
      setFiltersTable([]);
      setCurrentReport({});
      setSortTable({});
      setCurrentFunnel([]);
      setCurrentSegment(null);
      setMetricsChecked({ data: [] });
      // Отменяем незавершённые запросы
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // ==================== Загрузка дашборда по URL ====================
  useEffect(() => {
    if (isGlobalLoading) return;
    const hashId = searchParams.get(QUERY_NAME_DASHBOARD);
    if (!hashId) return;
    if (currentDashboard?.hash === hashId) return;
    if (currentDashboard?.hash && !hashId) {
      setSearchParams({
        [QUERY_NAME_DASHBOARD]: currentDashboard?.hash,
        [QUERY_FROM_MENU]: 'true',
      });
      return;
    }
    const isHome = !searchParams.get(QUERY_NAME_HOME);

    // Отменяем предыдущий запрос
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const openOverview = (hash: string) => {
      return unApi
        .getReportDataFromHash(hash)
        .then((response) => {
          if (controller.signal.aborted) return;
          setCurrentDashboard(response.data[0]);
          response.data[0]?.values?.detailsDashboard &&
            setDashboardDetails(response.data[0]?.values?.detailsDashboard);
          if (isHome) {
            searchParams.delete(QUERY_FROM_MENU);
            searchParams.delete(QUERY_NAME_HOME);
          }
          if (mainDashboardHash === hashId) {
            setSearchParams({ [QUERY_NAME_DASHBOARD]: hash, [QUERY_FROM_MENU]: 'true' });
            return;
          }
          setSearchParams({ [QUERY_NAME_DASHBOARD]: hash });
        })
        .catch(() => {
          if (controller.signal.aborted) return;
          setCurrentDashboard({});
          showErrorMessage('Данный дашборд удален');
        });
    };

    isMountRef.current = true;
    if (hashId && hashId !== 'none') {
      openOverview(hashId);
      sessionStorage.setItem('dashboard_hash', hashId);
    }
  }, [searchParams, isGlobalLoading]);

  // ==================== Загрузка блоков дашборда ====================
  useEffect(() => {
    if (Object.keys(currentDashboard)?.length > 0) {
      const rawBlocks = (currentDashboard?.values?.blocks || []) as DashboardComponent[];
      const normalizedBlocks = rawBlocks.map((block) => {
        const mc = normalizeMetricRefs(block?.data?.metricsChecked || []);
        return {
          ...block,
          data: {
            ...block.data,
            metricsChecked: mc,
          },
        };
      });
      setDashboardComponents(normalizedBlocks);
      handleCreatingBlocksState(normalizedBlocks);

      // Восстанавливаем масштаб дашборда
      const savedZoom = currentDashboard?.values?.zoom;
      if (savedZoom && typeof savedZoom === 'number' && savedZoom !== 1) {
        setScale(savedZoom);
      } else {
        setScale(1);
      }

      // Восстанавливаем сегмент дашборда
      const savedSegment = currentDashboard?.values?.segment;
      if (savedSegment && Object.keys(savedSegment).length > 0) {
        setCurrentSegment(savedSegment);
      }
    }
  }, [currentDashboard.id]);

  // ==================== Создание состояния блоков ====================
  const handleCreatingBlocksState = useCallback(
    (blocks: DashboardComponent[]) => {
      const dashboardComponentsIds: string[] = blocks.map((el) => el.data.blockId);
      setDashboardsBlocksTableInitialState({ ids: dashboardComponentsIds });
      setDashboardsBlocksInitialState(dashboardComponentsIds);

      blocks.forEach((component) => {
        const {
          attribution: blockAttribution,
          details,
          blockId,
          chartType,
          isAxesVisible,
          metricsChecked,
          groupsChecked,
          groupings,
          metrics,
          selectedGroupings,
          selectedMetrics,
          percentages,
          sparklines,
          cellFills,
          analysis,
          cellFilling,
          filtersTable,
          sortTable,
          totalChecked,
          filters: blockFilters,
          groupsSelectionMode: savedGroupsSelectionMode,
          selectFirstNRows: savedSelectFirstNRows,
          columnWidth: savedColumnWidth,
          columnsWidth: savedColumnsWidth,
        } = component.data;

        const effectiveGroupings = selectedGroupings || groupings;
        const effectiveMetrics = selectedMetrics || metrics;

        const chartData: Partial<{ chartType: string; isAxesVisible: boolean }> = {};
        if (chartType) chartData.chartType = chartType;
        if (isAxesVisible !== undefined) chartData.isAxesVisible = isAxesVisible;

        setDashboardsBlocksChartState({
          ids: [blockId],
          data: Object.keys(chartData).length > 0 ? chartData as any : undefined,
        });
        setAttributesDashboardBlocks({
          blockId,
          attribution: blockAttribution?.attribution as any || '',
          type_applications: blockAttribution?.type_applications as any || '',
        });
        setDashboardBlockDetails({
          blockId,
          detail: details as string,
        });

        if (blockFilters && Array.isArray(blockFilters) && blockFilters.length > 0) {
          setFilters({ data: blockFilters, blockId });
        }

        const effectiveTotalChecked = Array.isArray(totalChecked)
            ? totalChecked
            : [{ label: 'Итого и среднее', pathStr: 'total' }];

        let effectiveMetricsChecked = metricsChecked;
        if (
          (!metricsChecked || !Array.isArray(metricsChecked) || metricsChecked.length === 0) &&
          component.componentType === 'chart'
        ) {
          const metricsSource = effectiveMetrics || [];
          if (metricsSource.length > 0) {
            const firstMetric = metricsSource[0];
            effectiveMetricsChecked = [
              {
                dataKey: firstMetric?.dataKey || firstMetric?.key,
                name: firstMetric?.dataKey || firstMetric?.key,
                label: firstMetric?.name || firstMetric?.label,
              },
            ];
          }
        }

        const effectiveGroupsSelectionMode = savedGroupsSelectionMode || 'manual';

        loadBlockTableData({
          blockId,
          data: {
            ...(effectiveMetricsChecked &&
              effectiveMetricsChecked.length > 0 && { metricsChecked: effectiveMetricsChecked }),
            // Для режимов 'all' / 'firstN' не восстанавливаем конкретные groupsChecked —
            // они будут определены динамически при загрузке данных таблицы
            ...(effectiveGroupsSelectionMode === 'manual' && groupsChecked && { groupsChecked }),
            groupings: effectiveGroupings || [],
            metrics: effectiveMetrics || [],
            // Явные пустые значения: setDashboardsBlocksTableInitialState копирует блокам состояние
            // первого блока, и без них старый блок (без этих полей в сохранении) получал чужие «%»,
            // спарклайны и заливку
            percentages: Array.isArray(percentages) ? percentages : [],
            sparklines: Array.isArray(sparklines) ? sparklines : [],
            cellFills: cellFills && typeof cellFills === 'object' && !Array.isArray(cellFills) ? cellFills : {},
            analysis: analysis === true,
            cellFilling: cellFilling === true,
            ...(filtersTable && { filtersTable }),
            ...(sortTable && { sortTable }),
            ...(savedColumnWidth && { columnWidth: savedColumnWidth }),
            ...(savedColumnsWidth && { columnsWidth: savedColumnsWidth }),
            totalChecked: effectiveTotalChecked,
            groupsSelectionMode: effectiveGroupsSelectionMode,
            selectFirstNRows: savedSelectFirstNRows ?? null,
          },
        });
      });
    },
    []
  );

  // ==================== Обновление данных ====================
  const handleUpdateDashboardData = useCallback(() => {
    forceUpdate();
    baseHandleUpdateDashboardData();
  }, [forceUpdate, baseHandleUpdateDashboardData]);

  // Автоматический запрос при изменении параметров в свёрнутом режиме настроек
  useEffect(() => {
    if (isSettingsCollapsed && !fetchDashboardDataButtonVisible) {
      handleUpdateDashboardData();
    }
  }, [isSettingsCollapsed, fetchDashboardDataButtonVisible, handleUpdateDashboardData]);

  // ==================== Первоначальная загрузка ====================
  useEffect(() => {
    if (editBlockState.visible || addBlockVisible.visible) return;

    const dashboardId = currentDashboard?.id;
    if (!dashboardId || initialLoadDoneRef.current === dashboardId) return;

    initialLoadDoneRef.current = dashboardId;

    const blocksToRefresh = currentDashboard?.values?.blocks || [];
    blocksToRefresh.forEach((block: DashboardComponent) => {
      switch (block.componentType) {
        case 'table':
        case 'number':
          setMetaTableFetching({ value: true, blockId: block.data.blockId });
          break;
        case 'chart':
          setMetaChartFetching({ value: true, blockId: block.data.blockId });
          // Pie/bar/treemap/tunnel/speedometer зависят от tableData —
          // триггерим загрузку таблицы для скрытого ReportTable в DashboardBlock
          setMetaTableFetching({ value: true, blockId: block.data.blockId });
          break;
      }
    });

    setFetchDashboardDataButtonVisible(true);
    safeSetTimeout(() => {
      isMountRef.current = false;
    }, 100);
  }, [editBlockState.visible, addBlockVisible.visible, currentDashboard?.id]);

  // ==================== Фиксированный заголовок ====================
  useEffect(() => {
    const handleScroll = throttle(() => {
      if (paramsRef.current) {
        const scrollY = window.scrollY || window.pageYOffset;
        setIsFixedHeader(scrollY >= 280);
      }
    }, 200);

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ==================== Период из дашборда ====================
  useEffect(() => {
    if (currentDashboard?.values?.period) {
      const savedPeriod = currentDashboard.values.period;
      const newPeriod: any = {
        start: savedPeriod.start ?? '',
        end: savedPeriod.end ?? '',
        interval: savedPeriod.interval ?? '',
        select: savedPeriod.select ?? '',
      };
      // Сохраняем days/nStartTime/nEndTime/hours для корректного отображения пресетов
      if (savedPeriod.days) {
        newPeriod.days = savedPeriod.days;
      }
      if (savedPeriod.nStartTime != null) {
        newPeriod.nStartTime = savedPeriod.nStartTime;
        newPeriod.nEndTime = savedPeriod.nEndTime;
      }
      if (savedPeriod.hours) {
        newPeriod.hours = savedPeriod.hours;
      }
      safeLocalStorage.setItem('current_un_period', JSON.stringify(newPeriod));
      if (savedPeriod?.start && savedPeriod?.end) {
        setPeriod(newPeriod);
      }
    }
  }, [currentDashboard.id]);

  // ==================== Помощь ====================
  const handleOpenHelpFrame = useCallback(() => {
    // На «Сводке» (главный/overview дашборд) открываем отдельную инструкцию,
    // на обычных «Дашбордах» — прежнюю ссылку
    const isOverview = Boolean(mainDashboardHash) && currentDashboard?.hash === mainDashboardHash;
    setDrawerLink(isOverview ? STATIC_LINK_DRAWER.svodka : STATIC_LINK_DRAWER.dashboards);
    setAppReportOpen(true);
  }, [currentDashboard?.hash, mainDashboardHash]);

  // ==================== Мемоизированные параметры ====================
  const memorizedParams = useMemo(
    () => (
      <DashboardParams
        withCompare={false}
        withDetails={true}
        withSegments={true}
        isFilterVisible={true}
        detailsItems={dashboardChartDetails}
        attributionItems={attrItems}
        filtersFilter={[
          'Пользователи',
          'Регистрации',
          'Заявки',
          'Заказы',
          'Платежи',
          'Вебинары',
          'Реклама',
          'Посещение страницы',
        ]}
        setUpdateDashboard={handleUpdateDashboardData}
        fetchDashboardDataButtonVisible={fetchDashboardDataButtonVisible}
        setFetchDashboardDataButtonVisible={setFetchDashboardDataButtonVisible}
        isCollapsed={isSettingsCollapsed}
        compactStyle={true}
      />
    ),
    [
      dashboardChartDetails,
      fetchDashboardDataButtonVisible,
      isSettingsCollapsed,
      handleUpdateDashboardData,
    ]
  );

  // ==================== Общий блок модалок ====================
  const modalsBlock = useMemo(
    () => (
      <FullReportModal
        visible={fullReportModalState.visible}
        onClose={handleCloseFullReport}
        title={fullReportModalState.title}
        blockId={fullReportModalState.blockId}
        date={fullReportModalState.date}
        reportType={getReportTypeFromDate(fullReportModalState.date)}
        showChart={fullReportModalState.componentType === 'chart'}
        showTable={
          fullReportModalState.componentType === 'table' ||
          fullReportModalState.componentType === 'chart'
        }
      />
    ),
    [
      fullReportModalState,
      handleCloseFullReport,
    ]
  );

  // ==================== Рендер ====================
  const dashboardContent = (
    <div className={isFullscreen ? styles.dashboardPageFullscreen : styles.dashboardPage}>
      <DashboardToolbar
        dashboardName={currentDashboard?.values?.name}
        currentDashboard={dashboardMenuItem}
        scale={scale}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onResetZoom={resetZoom}
        isSettingsCollapsed={isSettingsCollapsed}
        onToggleSettings={() => setIsSettingsCollapsed(!isSettingsCollapsed)}
        isFullscreen={isFullscreen}
        onToggleFullscreen={toggleFullscreen}
        isFixedHeader={isFixedHeader}
        setAddBlockVisible={setAddBlockVisible}
        onOpenHelp={handleOpenHelpFrame}
        paramsComponent={memorizedParams}
        onRefresh={handleUpdateDashboardData}
        onSave={saveDashboard}
        saveAsModalVisible={saveAsModalVisible}
        onCloseSaveAsModal={() => setSaveAsModalVisible(false)}
      />

      <div ref={paramsRef} />

      {dashboardComponents.length === 0 && !isLoading && (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateArrow}>&#8593;</div>
          <div className={styles.emptyStateTitle}>Дашборд пуст</div>
          <div className={styles.emptyStateDesc}>
            Нажмите красную кнопку «+» справа вверху, чтобы добавить первый блок
          </div>
        </div>
      )}

      <DashboardGrid
        dashboardComponents={dashboardComponents}
        layout={layout}
        gridCols={gridCols}
        gridWidth={gridWidth}
        calculatedHeight={calculatedHeight}
        scale={scale}
        isDragging={isDragging}
        allowResize={allowResize}
        onLayoutChange={onLayoutChange}
        onDragStart={onDragStartWithHistory}
        onDragStop={onDragStopWithSelection}
        onResizeStart={onResizeStartWithHistory}
        onResize={onResize}
        onResizeStop={onResizeStop}
        onEditBlock={handleEdit}
        onCloneBlock={handleClone}
        onDeleteBlock={handleDelete}
        onOpenFullReport={handleOpenFullReport}
        isFullscreen={isFullscreen}
        selectedBlockIds={selectedBlockIds}
        onDeselectAll={handleDeselectAll}
        onDrag={onDragHandler}
      />

      {addBlockVisible.visible && (
        <AddBlockModal
          blockType={addBlockVisible.blockType}
          visible={addBlockVisible.visible}
          setVisible={setAddBlockVisible}
          onAddBlock={handleAddBlock}
          copyData={addBlockVisible.copyData}
        />
      )}

      {editBlockState.visible && (
        <EditBlockModal
          editState={editBlockState}
          setEditState={setEditBlockState}
          onEditBlock={handleEditBlock}
        />
      )}
    </div>
  );

  return (
    <MainLayout pageTitle="Дашборды">
      <Loader loading={isLoading}>{dashboardContent}</Loader>
      {modalsBlock}
      {isCreateMetricsVisible && (
        <CreateMetricsModal title="Создание метрики" />
      )}
    </MainLayout>
  );
};

export default Dashboards;
